/**
 * Partnerships Hub → Paquetes: reglas puras de orden y normalización (feedback de
 * Partnerships, 07/10/2026).
 *
 *  - Los códigos que se repiten (tipo de cuenta GCS / LCS, tier) se comparan y se
 *    guardan en MAYÚSCULAS y sin espacios de más: "gcs" y "GCS" eran dos filtros
 *    distintos y el Platinum de GCS Argentina no aparecía por una minúscula.
 *  - Orden de los paquetes: primero el orden manual (`sortOrder`, si alguien lo
 *    cargó), después el tier de más chico a más grande y por último el nombre.
 *
 * Tests: `npm run test:partnerships` (backend).
 */
import { foldKey } from './text-normalize';
import { todayUTC } from './dates';
import type { Package, PackageEligibility, PackageVariant, UpdatePackageInput } from './types/partnerships';

/** "  gcs " → "GCS". Vacío → undefined (así no se guarda un string vacío). */
export function normCode(value: string | null | undefined): string | undefined {
  const v = (value ?? '').replace(/\s+/g, ' ').trim().toUpperCase();
  return v || undefined;
}

/** Tipos de cuenta de Google Sold. */
export const SALE_SUB_TYPES = ['GCS', 'LCS'] as const;

/**
 * Tiers de más chico a más grande, por familia (cada familia se muestra en su
 * propio tipo de venta, así que no compiten entre sí):
 *  - Google GCS: Spotlight → Velocity → Custom → Premium → Platinum
 *  - Google Agency Sold: Bronze → Silver → Gold (→ Platinum)
 *  - TikTok: Essential → Standard → Launch → Creator → Funnel
 *  - Meta: Creadores → Advocates
 * Platinum va al final porque es el más grande en las dos familias de Google.
 */
export const TIER_ORDER = [
  'ESSENTIAL', 'STANDARD', 'LAUNCH', 'CREATOR', 'FUNNEL',
  'CREADORES', 'ADVOCATES',
  'BRONZE', 'SILVER', 'GOLD',
  'SPOTLIGHT', 'VELOCITY', 'CUSTOM', 'PREMIUM', 'PLATINUM',
] as const;

/** Posición del tier en `TIER_ORDER`; un tier desconocido va después de los conocidos. */
export function tierRank(tier: string | null | undefined): number {
  const i = (TIER_ORDER as readonly string[]).indexOf(normCode(tier) ?? '');
  return i < 0 ? TIER_ORDER.length : i;
}

export interface SortablePackage {
  name: string;
  tier?: string;
  sortOrder?: number;
}

/** Orden manual → tier (chico a grande) → nombre. */
export function comparePackages(a: SortablePackage, b: SortablePackage): number {
  const sa = a.sortOrder ?? Number.POSITIVE_INFINITY;
  const sb = b.sortOrder ?? Number.POSITIVE_INFINITY;
  if (sa !== sb) return sa - sb;
  const ta = tierRank(a.tier);
  const tb = tierRank(b.tier);
  if (ta !== tb) return ta - tb;
  return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
}

/** Tiers distintos (sin duplicados por mayúsculas), ordenados de chico a grande. */
export function sortedTiers(tiers: Array<string | null | undefined>): string[] {
  const seen = new Map<string, string>();
  for (const t of tiers) {
    const k = normCode(t);
    if (k && !seen.has(k)) seen.set(k, k);
  }
  return [...seen.values()].sort((a, b) => tierRank(a) - tierRank(b) || a.localeCompare(b));
}

// ── Edición: qué se guarda ──────────────────────────────────────────────────

/** Tier de la variante en mayúsculas (mismo motivo que el del pack). */
export function normalizeVariant(v: PackageVariant): PackageVariant {
  return { ...v, tier: normCode(v.tier) };
}

/**
 * Cambios de un paquete listos para guardar: códigos (tier, GCS/LCS) en mayúsculas
 * ("gcs" duplicaba el filtro) y `null` o `''` = BORRAR el campo (vaciar Orden o tier en
 * el editor no hacía nada: el JSON tiraba la clave y quedaba el valor viejo; el 13/10
 * pasaba lo mismo con todos los campos de texto de la ficha). La clave queda en
 * `undefined` y `removeUndefinedValues` la saca del Put. Puro: lo testea
 * partnerships-mapping.test.ts.
 */
export function packageChangesToSave(changes: Omit<UpdatePackageInput, 'expectedUpdatedAt'>): Partial<Package> {
  const { tier, saleSubType, variants, ...rest } = changes;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rest)) out[k] = v === null || v === '' ? undefined : v;
  if (tier !== undefined) out.tier = normCode(tier);
  if (saleSubType !== undefined) out.saleSubType = normCode(saleSubType);
  if (variants !== undefined) out.variants = variants.map(normalizeVariant);
  return out as Partial<Package>;
}

// ── Packs con variantes (pedido de Partnerships 13/10/2026) ─────────────────
//
// Hasta el 13/10 cada tier era una fila y una tarjeta ("Package A - AI Persona
// Motivators", "Package B - …"). Ahora un pack es UNA fila con `variants[]`; estas
// reglas puras parten los nombres viejos, agrupan y fusionan filas (las usa el
// script backend/scripts/partnerships-merge-packs.ts) y resumen las variantes para
// la tarjeta y los filtros. Tests en backend/src/lib/partnerships-mapping.test.ts.

type PackRowLike = {
  name: string;
  tier?: string;
  deliverables?: string[];
  pricing?: string;
  currency?: string;
  videos?: number;
  minCreators?: number;
  productionFee?: string;
  mediaCommitment?: string;
};

export interface PackNameParts {
  pack: string;
  variant: string;
  tier?: string;
}

function knownTier(word: string | null | undefined): string | undefined {
  const k = normCode(word);
  return k && (TIER_ORDER as readonly string[]).includes(k) ? k : undefined;
}

/**
 * Parte el nombre de una fila vieja en pack + variante:
 *  - "Package B - AI Persona Motivators" → pack "AI Persona Motivators", variante "Package B"
 *  - "M - Creator Led Package (CLP)"     → pack "Creator Led Package (CLP)", variante "M"
 *  - "Agency Sold SPLA Gold" (tier GOLD) → pack "Agency Sold SPLA", variante "Gold"
 *  - "LCS Bronze" (sin tier cargado)     → pack "LCS", variante "Bronze", tier BRONZE
 *  - "Platinum" / "YouTube Masthead"     → null (no hay pack + variante)
 * Si el tier cargado no coincide con la última palabra, no se parte: no se adivina.
 */
export function splitPackName(name: string, tier?: string | null): PackNameParts | null {
  const n = (name ?? '').replace(/\s+/g, ' ').trim();
  const dash = n.match(/^(.+?)\s[-–—]\s(.+)$/);
  if (dash) {
    const variant = dash[1].trim();
    return { pack: dash[2].trim(), variant, tier: normCode(tier) ?? knownTier(variant) };
  }
  const words = n.split(' ');
  if (words.length < 2) return null;
  const last = words[words.length - 1];
  const lastTier = knownTier(last);
  const t = normCode(tier);
  if (!lastTier || (t && t !== lastTier)) return null;
  return { pack: words.slice(0, -1).join(' '), variant: last, tier: lastTier };
}

export interface PackGroup<T> {
  packName: string;
  key: string;
  rows: Array<{ row: T; variant: string; tier?: string }>;
}

/**
 * Agrupa filas por pack según el nombre (clave sin tildes ni mayúsculas; `aliases`
 * une packs escritos distinto, p. ej. "Creator Led Packaged (CLP)" → "Creator Led
 * Package (CLP)"). Un pack con una sola fila no se fusiona: va a `singles`, igual
 * que las filas que no se parten. Dentro del grupo: tier de chico a grande, después
 * nombre de la variante.
 */
export function groupPackRows<T extends { name: string; tier?: string }>(
  rows: readonly T[],
  aliases: Record<string, string> = {},
): { groups: PackGroup<T>[]; singles: T[] } {
  const aliasByKey = new Map(Object.entries(aliases).map(([from, to]) => [foldKey(from), to]));
  const byKey = new Map<string, PackGroup<T>>();
  const singles: T[] = [];
  for (const row of rows) {
    const parts = splitPackName(row.name, row.tier);
    if (!parts) { singles.push(row); continue; }
    const packName = aliasByKey.get(foldKey(parts.pack)) ?? parts.pack;
    const key = foldKey(packName);
    const g = byKey.get(key) ?? { packName, key, rows: [] };
    g.rows.push({ row, variant: parts.variant, tier: parts.tier });
    byKey.set(key, g);
  }
  const groups: PackGroup<T>[] = [];
  for (const g of byKey.values()) {
    if (g.rows.length < 2) { singles.push(...g.rows.map((r) => r.row)); continue; }
    g.rows.sort((a, b) => tierRank(a.tier) - tierRank(b.tier) || a.variant.localeCompare(b.variant, 'es', { numeric: true, sensitivity: 'base' }));
    groups.push(g);
  }
  return { groups, singles };
}

function firstNonEmpty<T>(values: Array<T | undefined | null>): T | undefined {
  for (const v of values) {
    if (v == null) continue;
    if (typeof v === 'string' && !v.trim()) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    return v;
  }
  return undefined;
}

function unionUnique(lists: Array<readonly string[] | undefined>): string[] {
  const seen = new Map<string, string>();
  for (const l of lists) for (const s of l ?? []) {
    const k = foldKey(s);
    if (k && !seen.has(k)) seen.set(k, s);
  }
  return [...seen.values()];
}

/**
 * Fusiona las filas de un grupo en UN pack: lo que cambia entre tiers pasa a
 * `variants` (una por fila, `variantId` = el packageId viejo, que queda también en
 * `legacyPackageId`); lo común se toma de la primera fila que lo tenga (listas:
 * unión sin duplicados). El pack conserva el `packageId` de la primera fila, así
 * los links a ese id siguen andando; los demás ids los redirige el tombstone
 * `mergedInto`. El `tier` del pack queda vacío: ahora vive en cada variante.
 */
/** Campos que difieren entre las filas de un grupo y conviene mirar antes de fusionar. */
export function groupDivergences(rows: readonly Package[]): string[] {
  const differs = (get: (r: Package) => unknown) => new Set(rows.map((r) => JSON.stringify(get(r) ?? null))).size > 1;
  const out: string[] = [];
  if (differs((r) => r.status)) out.push('status');
  if (differs((r) => r.saleType)) out.push('saleType');
  if (differs((r) => normCode(r.saleSubType))) out.push('saleSubType');
  if (differs((r) => r.programId)) out.push('programId');
  if (differs((r) => r.description?.trim())) out.push('description');
  return out;
}

/** Bloquean la fusión salvo `--force`: un pack no puede mezclar tipos de venta ni estados. */
export const BLOCKING_DIVERGENCES = ['status', 'saleType', 'saleSubType'] as const;

export function mergePackRows<T extends Package>(packName: string, parts: PackGroup<T>['rows']): T {
  const rows = parts.map((p) => p.row);
  const kept = rows[0];
  // El pack no puede ser MÁS visible que su fila más restrictiva.
  const status = rows.some((r) => r.status === 'ARCHIVED') ? 'ARCHIVED' : rows.some((r) => r.status === 'DRAFT') ? 'DRAFT' : 'PUBLISHED';
  const variants: PackageVariant[] = parts.map(({ row, variant, tier }) => ({
    variantId: row.packageId,
    name: variant,
    tier: tier ?? normCode(row.tier),
    deliverables: row.deliverables?.length ? row.deliverables : undefined,
    pricing: row.pricing || undefined,
    currency: row.currency || undefined,
    videos: row.videos || undefined,
    minCreators: row.minCreators || undefined,
    productionFee: row.productionFee || undefined,
    mediaCommitment: row.mediaCommitment || undefined,
    notes: row !== kept && row.notes && row.notes !== kept.notes ? row.notes : undefined,
    legacyPackageId: row.packageId,
  }));
  const driveUrl = firstNonEmpty(rows.map((r) => r.driveUrl));
  const links = unionUnique([
    (kept.links ?? []).map((l) => `${l.label}\u0000${l.url}`),
    parts.filter((p) => p.row.driveUrl && p.row.driveUrl !== driveUrl).map((p) => `One-pager ${p.variant}\u0000${p.row.driveUrl}`),
  ]).map((s) => { const [label, url] = s.split('\u0000'); return { label, url }; });
  return {
    ...kept,
    name: packName,
    status,
    tier: undefined,
    variants,
    deliverables: undefined,
    pricing: undefined,
    currency: undefined,
    videos: undefined,
    minCreators: undefined,
    productionFee: undefined,
    mediaCommitment: undefined,
    description: firstNonEmpty(rows.map((r) => r.description)) ?? '',
    targetAudience: firstNonEmpty(rows.map((r) => r.targetAudience)) ?? '',
    availableMarkets: unionUnique(rows.map((r) => r.availableMarkets)),
    highlights: unionUnique(rows.map((r) => r.highlights)),
    includedProducts: unionUnique(rows.map((r) => r.includedProducts)),
    driveUrl,
    links: links.length ? links : undefined,
    programId: firstNonEmpty(rows.map((r) => r.programId)),
    saleType: firstNonEmpty(rows.map((r) => r.saleType)),
    saleSubType: firstNonEmpty(rows.map((r) => r.saleSubType)),
    sortOrder: rows.map((r) => r.sortOrder).filter((n): n is number => n != null).sort((a, b) => a - b)[0],
  } as T;
}

/** Las variantes del pack; un pack sin `variants` pero con números/precio propios cuenta como UNA variante. */
export function effectiveVariants(pkg: PackRowLike & { variants?: PackageVariant[] }): PackageVariant[] {
  if (pkg.variants?.length) return pkg.variants;
  const hasOwn = pkg.videos || pkg.minCreators || pkg.pricing || pkg.productionFee || pkg.mediaCommitment || pkg.deliverables?.length;
  if (!hasOwn) return [];
  return [{
    variantId: '__pack__',
    name: pkg.tier ?? '',
    tier: normCode(pkg.tier),
    deliverables: pkg.deliverables,
    pricing: pkg.pricing,
    currency: pkg.currency,
    videos: pkg.videos,
    minCreators: pkg.minCreators,
    productionFee: pkg.productionFee,
    mediaCommitment: pkg.mediaCommitment,
  }];
}

/** Tiers del pack (de sus variantes, o el propio) para los badges del selector por tipo de venta. */
export function packageTiers(pkg: PackRowLike & { variants?: PackageVariant[] }): string[] {
  const own = pkg.variants?.length ? pkg.variants.map((v) => v.tier) : [pkg.tier];
  return sortedTiers(own);
}

export interface VariantSummary {
  count: number;
  /** Rango de videos entre variantes (null si ninguna lo carga). */
  videos: { min: number; max: number } | null;
  /** Precios distintos, en el orden de las variantes (para "USD 3.500 – 8.300"). */
  pricing: string[];
}

export function variantSummary(pkg: PackRowLike & { variants?: PackageVariant[] }): VariantSummary {
  const vs = effectiveVariants(pkg);
  const videos = vs.map((v) => v.videos).filter((n): n is number => typeof n === 'number' && n > 0);
  const pricing = [...new Set(vs.map((v) => (v.pricing ? `${v.currency ? `${v.currency} ` : ''}${v.pricing}` : '')).filter(Boolean))];
  return {
    count: vs.length,
    videos: videos.length ? { min: Math.min(...videos), max: Math.max(...videos) } : null,
    pricing,
  };
}

// ── Elegibilidad como filtro ────────────────────────────────────────────────

export interface EligibilitySelection {
  segments: string[];
  accountSizes: string[];
  clientTypes: string[];
}

export const EMPTY_ELIGIBILITY_SELECTION: EligibilitySelection = { segments: [], accountSizes: [], clientTypes: [] };

type EligibilityLike = { eligibility?: PackageEligibility };

/** Valores de elegibilidad presentes en los packs, con cuántos packs tiene cada uno (opciones de los filtros). */
export function eligibilityFacets(pkgs: readonly EligibilityLike[]): Record<keyof EligibilitySelection, Map<string, number>> {
  const out = { segments: new Map<string, number>(), accountSizes: new Map<string, number>(), clientTypes: new Map<string, number>() };
  const bump = (m: Map<string, number>, v: string | undefined) => { if (v) m.set(v, (m.get(v) ?? 0) + 1); };
  for (const p of pkgs) {
    for (const s of p.eligibility?.segments ?? []) bump(out.segments, s);
    for (const s of p.eligibility?.accountSizes ?? []) bump(out.accountSizes, s);
    bump(out.clientTypes, p.eligibility?.clientType);
  }
  return out;
}

/** Sin selección = pasa todo. `clientType: 'both'` responde a "new" y a "repeated". */
export function matchesEligibility(pkg: EligibilityLike, sel: EligibilitySelection): boolean {
  const e = pkg.eligibility ?? {};
  if (sel.segments.length && !sel.segments.some((s) => (e.segments ?? []).includes(s))) return false;
  if (sel.accountSizes.length && !sel.accountSizes.some((s) => (e.accountSizes ?? []).includes(s))) return false;
  if (sel.clientTypes.length) {
    const ct = e.clientType;
    const ok = sel.clientTypes.some((s) => s === ct || (ct === 'both' && (s === 'new' || s === 'repeated')) || (s === 'both' && ct === 'both'));
    if (!ok) return false;
  }
  return true;
}

/** Vencido = `validUntil` anterior a hoy (fechas ISO date-only, comparación lexicográfica). */
export function isPackageExpired(pkg: { validUntil?: string }, today: string = todayUTC()): boolean {
  return !!pkg.validUntil && /^\d{4}-\d{2}-\d{2}/.test(pkg.validUntil) && pkg.validUntil.slice(0, 10) < today;
}
